import {REGEX_EMAIL, REGEX_MOBILE_PHONE, REGEX_MOTO_PLATE, REGEX_SPAIN_POSTALCODE} from '../../base.js'
import {
  colourDatalist,
  defaultTabIndex,
  displayTypes,
  environmentalLabelDatalist,
  formFieldContexts,
  formFieldIds,
  inputTypes,
  PATTERN_CONSTRAINT_AD_TITLE_ALLOWED_CHARACTERS,
  PATTERN_CONSTRAINT_MIN_PRICE_300
} from './values.js'

const {
  adTitle,
  comments,
  makeId,
  modelId,
  fuelId,
  yearId,
  versionId,
  hidePhone,
  kilometersId,
  postalCode,
  price,
  name,
  colour,
  colourExtra,
  email,
  environmentalLabel,
  phone,
  plate,
  dataPrivacyCheckbox,
  reliableInfoCheckbox
} = formFieldIds

// CONSTRAINTS GENERATORS

export const getMaxLengthConstraint = ({value}) => ({
  property: {
    maxlength: value
  }
})

export const getMinLengthConstraint = ({message, value}) => ({
  message,
  property: {
    minlength: value
  }
})

export const getNotNullConstraint = ({message}) => ({
  message,
  property: {
    notnull: ''
  }
})

export const getPatternConstraint = ({message, pattern}) => ({
  message,
  property: {
    pattern
  }
})

export const configFields = {
  [comments]: {
    display: displayTypes.multiline,
    id: comments,
    label: 'FORM.COMMENTS_LABEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: {
      maxLength: {value: '3000'},
      notNull: {message: 'FORM.REQUIRED_FIELD_ERROR_TEXT.COMMENTS'}
    }
  },
  [dataPrivacyCheckbox]: {
    display: displayTypes.checkbox,
    id: dataPrivacyCheckbox,
    label: 'FORM.DATA_PRIVACY_CHECKBOX_LABEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: {
      notNull: {message: 'FORM.REQUIRED_FIELD_ERROR_TEXT.DATA_PRIVACY_CHECKBOX'}
    }
  },
  [email]: {
    display: displayTypes.email,
    hint: 'FORM.EMAIL_PLACEHOLDER',
    id: email,
    label: 'FORM.EMAIL_LABEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: {
      maxLength: {value: '80'},
      notNull: {message: 'FORM.REQUIRED_FIELD_ERROR_TEXT.EMAIL'},
      pattern: {message: 'FORM.EMAIL_TEXT_ERROR', pattern: REGEX_EMAIL.source}
    }
  },
  [kilometersId]: {
    hint: 'PUBLISH_AD.FB.KMS.MOTOS.HINT',
    id: kilometersId,
    label: 'COMMON.KMS_VEHICLE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: {
      maxLength: {value: '6'},
      notNull: {message: 'PUBLISH_AD.FB.KMS.CONSTRAINTS.NOTNULL'},
      pattern: {message: 'PUBLISH_AD.FB.KMS.CONSTRAINTS.MIN', pattern: /^[1-9][0-9]{3,}/.source}
    }
  },
  [name]: {
    hint: 'FORM.CONTACT_NAME_YOUR_NAME_PLACEHOLDER',
    id: name,
    label: 'FORM.CONTACT_NAME_LABEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: {
      maxLength: {value: '30'},
      notNull: {message: 'FORM.REQUIRED_FIELD_ERROR_TEXT.NAME'}
    }
  },
  [phone]: {
    hint: 'FORM.CONTACT_PHONE_WITH_EXAMPLE_PLACEHOLDER',
    id: phone,
    label: 'COMMON.PHONE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: {
      maxLength: {value: '9'},
      notNull: {message: 'INSTANT_OFFER.LANDING.PHONE_IS_REQUIRED'},
      pattern: {message: 'INSTANT_OFFER.LANDING.PHONE_NOT_VALID', pattern: REGEX_MOBILE_PHONE.source}
    }
  },
  [postalCode]: {
    hint: 'PUBLISH_AD.STANDALONE.FORM.POSTAL_CODE.HINT',
    id: postalCode,
    label: 'COMMON.POSTAL_CODE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: {
      maxLength: {value: '5'},
      notNull: {message: 'INSTANT_OFFER.LANDING.POSTAL_CODE_IS_REQUIRED'},
      pattern: {message: 'MODAL_LAST_CALL.VALIDATION_MESSAGES.POSTCODE_INVALID', pattern: REGEX_SPAIN_POSTALCODE.source}
    }
  },
  [reliableInfoCheckbox]: {
    display: displayTypes.checkbox,
    id: reliableInfoCheckbox,
    label: 'FORM.RELIABLE_INFO_CHECKBOX_LABEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: {
      notNull: {message: 'FORM.REQUIRED_FIELD_ERROR_TEXT.RELIABLE_INFO_CHECKBOX'}
    }
  }
}

export const getConfigFields = {
  [adTitle]: ({customProps} = {}) => ({
    help: 'PUBLISH_AD.FB.AD_TITLE.HELP',
    hint: 'PUBLISH_AD.FB.AD_TITLE.HINT',
    id: adTitle,
    label: 'PUBLISH_AD.FB.AD_TITLE.LABEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: [
      getNotNullConstraint({
        message: 'PUBLISH_AD.FB.AD_TITLE.CONSTRAINTS.NOTNULL'
      }),
      getMinLengthConstraint({
        message: 'PUBLISH_AD.FB.AD_TITLE.CONSTRAINTS.MINLENGTH',
        value: '10'
      }),
      getPatternConstraint({
        message: 'PUBLISH_AD.FB.AD_TITLE.CONSTRAINTS.PATTERN',
        pattern: PATTERN_CONSTRAINT_AD_TITLE_ALLOWED_CHARACTERS
      }),
      getMaxLengthConstraint({value: '50'})
    ],
    ...customProps
  }),
  [email]: ({customProps} = {}) => ({
    ...configFields[email],
    hint: 'COMMON.FORM_FIELDS.EMAIL.HINT',
    constraints: [
      getMaxLengthConstraint({value: '80'}),
      getNotNullConstraint({message: 'INSTANT_OFFER.LANDING.EMAIL_IS_REQUIRED'}),
      getPatternConstraint({message: 'FORM.EMAIL_TEXT_ERROR', pattern: REGEX_EMAIL.source})
    ],
    ...customProps
  }),
  [colour]: ({customProps} = {}) => ({
    datalist: colourDatalist,
    display: displayTypes.dropdown,
    hint: 'COMMON.SELECT',
    id: colour,
    label: 'PUBLISH_AD.FB.COLORS.MOTOS.LABEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: [
      getNotNullConstraint({
        message: 'PUBLISH_AD.FB.COLORS.CONSTRAINTS.NOTNULL'
      })
    ],
    ...customProps
  }),
  [colourExtra]: ({customProps} = {}) => ({
    hint: 'PUBLISH_AD.FB.COLORS_EXTRA.MOTOS.HINT',
    id: colourExtra,
    label: 'PUBLISH_AD.FB.COLORS_EXTRA.MOTOS.LABEL',
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: [getMaxLengthConstraint({value: '30'})],
    ...customProps
  }),
  [environmentalLabel]: ({customProps} = {}) => ({
    datalist: environmentalLabelDatalist,
    display: displayTypes.dropdown,
    hint: 'COMMON.SELECT',
    id: environmentalLabel,
    label: 'PUBLISH_AD.FB.ENVIRONMENTAL_LABELS.MOTOS.LABEL_OPTIONAL',
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    ...customProps
  }),
  [fuelId]: ({customProps} = {}) => ({
    context: formFieldContexts[fuelId],
    disabled: true,
    display: displayTypes.dropdown,
    hint: 'COMMON.SELECT',
    id: fuelId,
    label: 'COMMON.FUEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: [
      getNotNullConstraint({
        message: 'COMMON.FORM_FIELDS.FUELS.MOTOS.CONSTRAINTS.NOTNULL'
      })
    ],
    ...customProps
  }),
  [hidePhone]: ({customProps} = {}) => ({
    display: displayTypes.switch,
    id: hidePhone,
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    ...customProps
  }),
  [kilometersId]: ({customProps} = {}) => ({
    display: displayTypes.numeric,
    hint: 'PUBLISH_AD.FB.KMS.MOTOS.HINT',
    label: 'COMMON.FORM_FIELDS.KMS.MOTOS.LABEL',
    id: kilometersId,
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: [
      getNotNullConstraint({
        message: 'COMMON.FORM_FIELDS.KMS.MOTOS.CONSTRAINTS.NOTNULL'
      }),
      getMaxLengthConstraint({
        value: 7
      }),
      getPatternConstraint({
        message: 'COMMON.FORM_FIELDS.KMS.MOTOS.CONSTRAINTS.MIN',
        pattern: /^[\d.]{4,}$/.source
      })
    ],
    ...customProps
  }),
  [name]: ({customProps} = {}) => ({
    ...configFields[name],
    hint: '',
    label: 'FORM.CONTACT_NAME_OPTIONAL_LABEL',
    required: false,
    constraints: [getMaxLengthConstraint({value: '30'})],
    ...customProps
  }),
  [makeId]: ({customProps} = {}) => ({
    context: formFieldContexts[makeId],
    display: displayTypes.dropdownAutosuggest,
    hint: 'COMMON.SELECT_OR_WRITE',
    id: makeId,
    label: 'COMMON.MAKE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: [
      getNotNullConstraint({
        message: 'COMMON.FORM_FIELDS.MAKES.MOTOS.CONSTRAINTS.NOTNULL'
      })
    ],
    ...customProps
  }),
  [modelId]: ({customProps} = {}) => ({
    context: formFieldContexts[modelId],
    disabled: true,
    display: displayTypes.dropdownAutosuggest,
    hint: 'COMMON.SELECT_OR_WRITE',
    id: modelId,
    label: 'COMMON.PLURALS.MODEL',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: [
      getNotNullConstraint({
        message: 'COMMON.FORM_FIELDS.MODELS.MOTOS.CONSTRAINTS.NOTNULL'
      })
    ],
    ...customProps
  }),
  [phone]: ({customProps} = {}) => ({
    display: displayTypes.numeric,
    hint: 'FORM.CONTACT_PHONE_WITH_EXAMPLE_PLACEHOLDER',
    id: phone,
    label: 'COMMON.PHONE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: [
      getMaxLengthConstraint({value: '9'}),
      getNotNullConstraint({
        message: 'INSTANT_OFFER.LANDING.PHONE_IS_REQUIRED'
      }),
      getPatternConstraint({
        message: 'INSTANT_OFFER.LANDING.PHONE_NOT_VALID',
        pattern: REGEX_MOBILE_PHONE.source
      })
    ],
    ...customProps
  }),
  [plate]: ({customProps} = {}) => ({
    hint: 'COMMON.FORM_FIELDS.PLATE.HINT',
    id: plate,
    label: 'COMMON.PLATE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: [
      getMaxLengthConstraint({value: '8'}),
      getNotNullConstraint({
        message: 'COMMON.FORM_FIELDS.PLATE.MOTOS.CONSTRAINTS.NOTNULL'
      }),
      getPatternConstraint({
        message: 'FORM.PLATE.ERROR_NOT_VALID',
        pattern: REGEX_MOTO_PLATE.source
      })
    ],
    ...customProps
  }),
  [postalCode]: ({customProps} = {}) => ({
    display: displayTypes.numeric,
    hint: 'PUBLISH_AD.STANDALONE.FORM.POSTAL_CODE.HINT',
    id: postalCode,
    label: 'COMMON.POSTAL_CODE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: [
      getMaxLengthConstraint({value: 5}),
      getNotNullConstraint({message: 'INSTANT_OFFER.LANDING.POSTAL_CODE_IS_REQUIRED'}),
      getPatternConstraint({
        message: 'MODAL_LAST_CALL.VALIDATION_MESSAGES.POSTCODE_INVALID',
        pattern: REGEX_SPAIN_POSTALCODE.source
      })
    ],
    ...customProps
  }),
  [price]: ({customProps} = {}) => ({
    display: displayTypes.money,
    hint: 'PUBLISH_AD.FB.PRIZES.MOTOS.HINT',
    id: price,
    label: 'COMMON.PRICE',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.text,
    constraints: [
      getNotNullConstraint({
        message: 'PUBLISH_AD.FB.PRIZES.MOTOS.CONSTRAINTS.NOTNULL'
      }),
      getPatternConstraint({
        message: 'PUBLISH_AD.FB.PRIZES.MOTOS.CONSTRAINTS.MIN',
        pattern: PATTERN_CONSTRAINT_MIN_PRICE_300
      }),
      getMaxLengthConstraint({value: 7})
    ],
    ...customProps
  }),
  [versionId]: ({customProps} = {}) => ({
    context: formFieldContexts[versionId],
    disabled: true,
    display: displayTypes.dropdownAutosuggest,
    help: 'COMMON.FORM_FIELDS.VERSIONS.MOTOS.HELP',
    hint: 'COMMON.SELECT_OR_WRITE',
    id: versionId,
    label: 'COMMON.VERSION',
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: [
      getNotNullConstraint({
        message: 'COMMON.FORM_FIELDS.VERSIONS.MOTOS.CONSTRAINTS.NOTNULL'
      })
    ],
    ...customProps
  }),
  [yearId]: ({customProps} = {}) => ({
    context: formFieldContexts[yearId],
    disabled: true,
    display: displayTypes.dropdown,
    label: 'COMMON.FORM_FIELDS.YEARS.LABEL',
    hint: 'COMMON.SELECT',
    id: yearId,
    required: true,
    tabIndex: defaultTabIndex,
    type: inputTypes.picker,
    constraints: [
      getNotNullConstraint({
        message: 'COMMON.FORM_FIELDS.YEARS.MOTOS.CONSTRAINTS.NOTNULL'
      })
    ],
    ...customProps
  })
}

// COMMON CONFIGURATION RULES

const operators = {
  exists: 'EXISTS'
}

export const getEmptyFieldRule = ({hasAutoHide = false, id, remote = false}) => ({
  when: [{operator: operators.exists, id}],
  then: {
    data: {
      datalist: [],
      disabled: true,
      hidden: hasAutoHide,
      required: true,
      value: ''
    },
    remote
  }
})

export const getRemoteFieldRule = ({hasAutoHide, id}) => getEmptyFieldRule({hasAutoHide, id, remote: true})
